import { FormControlLabel, Switch } from '@mui/material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { L } from 'harmony-language';
import React from 'react';
import { SITE_CATEGORIES } from '../../../../../constants/constants';
import '../../../../../scss/bulk-creation.scss';
import { CargoType, LocationType } from '../../../../../types';
import { getWorkingWeeks } from '../../../../../utils/date-time-utils';
import { SelectCargoType } from '../../../../shared/inputs/select-cargo-type';
import { SelectLocationType } from '../../../../shared/inputs/select-location-type';
import { AgisticsMultiSelect } from '../../../../shared/multi-select/agistics-multi-select';
import type { BulkConfigurationSettings } from '../bulk-drafts-types';
import { LoadSize } from './load-size';
import { LoadWeight } from './load-weight';

interface BulkConfigurationProps {
    bulkConfiguration: BulkConfigurationSettings | null;
    setBulkConfiguration: (bulkConfiguration: BulkConfigurationSettings) => void;
}

export const BulkConfiguration = (props: BulkConfigurationProps) => {
    const { bulkConfiguration, setBulkConfiguration } = props;

    const [weeks, setWeeks] = React.useState<string[]>(bulkConfiguration?.weeks || []);
    const [siteType, setSiteType] = React.useState<LocationType | null>(bulkConfiguration?.siteType || null);
    const [cargoType, setCargoType] = React.useState<CargoType | null>(bulkConfiguration?.cargoType || null);
    const [loadSize, setLoadSize] = React.useState(bulkConfiguration?.loadSize || null);
    const [loadWeightGrams, setLoadWeightGrams] = React.useState(bulkConfiguration?.loadWeightGrams || null);
    const [checked, setChecked] = React.useState(bulkConfiguration?.checked || false);

    const menuItems = React.useMemo(() => getWorkingWeeks(13).map(week => ({
            id: week.sundayIso,
            value: week.sundayIso,
            label: week.sundayIso
        })), []);

    const nextTab = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (weeks.length && siteType && cargoType && (loadSize || loadWeightGrams)) {
            setBulkConfiguration({ weeks, siteType, cargoType, loadSize, checked, loadWeightGrams });
        }
    };

    const handleSwitchChanged = () => {
        setSiteType(null);
        setChecked(!checked);
    };

    const sizeWeightValidation = React.useCallback(() => {
        return !loadSize && !loadWeightGrams ? false : true;
    }, [loadSize, loadWeightGrams]);

    return (
        <div className='bulk-configuration' data-testid='bulk-configuration'>
            <h2>{L.bulkConfigHeader()}</h2>
            <Paper>
                <form onSubmit={(event) => nextTab(event)}>
                    <div className='bulk-title-bar justify-between'>
                        <h4>{L.bulkConfigTitle()}</h4>
                        <Button type='submit' variant='contained' color='primary'>
                            {L.bulkStartEntering()}
                        </Button>
                    </div>
                    <div className='bulk-config-inputs'>
                        <div style={{ position: 'relative', top: '10px' }}>
                            <FormControlLabel
                                control={<Switch
                                    checked={checked}
                                    onChange={() => handleSwitchChanged()}
                                />}
                                label={L.changeStopType()}
                                labelPlacement='start'
                                sx={{ margin: 0 }}
                            />
                        </div>
                        <span style={{ width: '18rem' }}>
                            <AgisticsMultiSelect
                                label={L.bulkSelectWeekOf()}
                                required
                                items={menuItems}
                                selectedIds={weeks}
                                setSelectedIds={setWeeks}/>
                        </span>
                        <SelectCargoType cargoType={cargoType}
                            handleChange={(newCargoType) => {
                                setCargoType(newCargoType);
                            }}
                            inputProps={{
                                'aria-label': L.selectCargo()
                            }}
                            label={L.selectCargo()}
                            required
                            style={{ minWidth: '8rem' }}/>
                        <SelectLocationType
                            label={`${checked ? L.origin() : L.destination()} ${L.siteType()}`}
                            required
                            locationType={siteType}
                            handleChange={setSiteType}
                            filters={{
                                category: SITE_CATEGORIES.SITE,
                                cargoTypeId: cargoType?.id,
                                isBasedOnOrigin: checked,
                            }}
                            style={{ minWidth: '12rem' }}/>
                        <LoadSize loadSize={loadSize} setLoadSize={setLoadSize} sizeWeightValidation={sizeWeightValidation} />
                        <LoadWeight
                            loadWeightGrams={loadWeightGrams}
                            setLoadWeightGrams={setLoadWeightGrams}
                            sizeWeightValidation={sizeWeightValidation} />
                    </div>
                </form>
            </Paper>
        </div>
    );
};